import { useCallback, useState } from "react";
import type { UploadConfig, UploadFile, UploadHandler, UploadStatus } from "./types";
import { generateId } from "./utils";

interface UseUploadOptions extends UploadConfig {
  handler: UploadHandler;
}

interface UseUploadReturn {
  files: UploadFile[];
  isUploading: boolean;
  addFiles: (files: File[]) => void;
  removeFile: (id: string) => void;
  retryFile: (id: string) => void;
  clearFiles: () => void;
  clearCompleted: () => void;
}

export function useUpload(options: UseUploadOptions): UseUploadReturn {
  const {
    handler,
    maxSize = 10 * 1024 * 1024,
    maxFiles = 10,
    accept,
  } = options;

  const [files, setFiles] = useState<UploadFile[]>([]);

  const updateFile = useCallback(
    (id: string, updates: Partial<UploadFile>) => {
      setFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, ...updates } : f))
      );
    },
    []
  );

  const uploadFile = useCallback(
    async (uploadFile: UploadFile) => {
      const { file, id } = uploadFile;

      try {
        // Validate file size
        if (file.size > maxSize) {
          updateFile(id, {
            status: "error",
            error: `File too large. Maximum size is ${Math.round(maxSize / 1024 / 1024)}MB`,
          });
          return;
        }

        // Update status to uploading
        updateFile(id, { status: "uploading" });

        // Get presigned URL
        const { uploadUrl, assetId } = await handler.getUploadUrl(file);

        // Upload to presigned URL with progress tracking
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const progress = Math.round((e.loaded / e.total) * 100);
              updateFile(id, { progress });
            }
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          };

          xhr.onerror = () => reject(new Error("Upload failed"));
          xhr.ontimeout = () => reject(new Error("Upload timed out"));

          xhr.open("PUT", uploadUrl);
          xhr.setRequestHeader(
            "Content-Type",
            file.type || "application/octet-stream"
          );
          xhr.send(file);
        });

        // Update status to processing
        updateFile(id, { status: "processing", progress: 100, assetId });

        // Confirm upload if handler provided
        let finalUrl: string | undefined;
        if (handler.onUploadComplete && assetId) {
          const result = await handler.onUploadComplete(assetId, file);
          finalUrl = result.url;
        }

        // Update status to complete
        updateFile(id, { status: "complete", url: finalUrl });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        updateFile(id, { status: "error", error: errorMessage });
        handler.onError?.(
          error instanceof Error ? error : new Error(errorMessage),
          file
        );
      }
    },
    [handler, maxSize, updateFile]
  );

  const addFiles = useCallback(
    (newFiles: File[]) => {
      // Filter files based on accept types
      let filesToAdd = newFiles;

      if (accept && accept.length > 0) {
        filesToAdd = newFiles.filter((file) => {
          return accept.some((type) => {
            if (type.startsWith(".")) {
              return file.name.toLowerCase().endsWith(type.toLowerCase());
            }
            if (type.endsWith("/*")) {
              return file.type.startsWith(type.slice(0, -1));
            }
            return file.type === type;
          });
        });
      }

      // Limit number of files
      const currentCount = files.length;
      const availableSlots = maxFiles - currentCount;
      filesToAdd = filesToAdd.slice(0, availableSlots);

      if (filesToAdd.length === 0) return;

      const uploadFiles: UploadFile[] = filesToAdd.map((file) => ({
        id: generateId(),
        file,
        progress: 0,
        status: "pending" as UploadStatus,
      }));

      setFiles((prev) => [...prev, ...uploadFiles]);

      // Start uploading each file
      for (const fileToUpload of uploadFiles) {
        void uploadFile(fileToUpload);
      }
    },
    [accept, files.length, maxFiles, uploadFile]
  );

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const retryFile = useCallback(
    (id: string) => {
      const file = files.find((f) => f.id === id);
      if (file && file.status === "error") {
        const newFile: UploadFile = {
          ...file,
          progress: 0,
          status: "pending",
          error: undefined,
        };
        setFiles((prev) =>
          prev.map((f) => (f.id === id ? newFile : f))
        );
        void uploadFile(newFile);
      }
    },
    [files, uploadFile]
  );

  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  const clearCompleted = useCallback(() => {
    setFiles((prev) => prev.filter((f) => f.status !== "complete"));
  }, []);

  const isUploading = files.some(
    (f) => f.status === "uploading" || f.status === "processing"
  );

  return {
    files,
    isUploading,
    addFiles,
    removeFile,
    retryFile,
    clearFiles,
    clearCompleted,
  };
}
